import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CalendarDays, CircleCheck, CircleX, FileText, Image, Mail, MailOpen, PlayCircle, Square, UploadCloud, UserRound, X } from "lucide-react";
import { isDemoMode } from "../../demo/demoMode";
import helpContent from "../../components/HelpCenter/helpContent";
import learningContent from "../../components/HelpCenter/learningContent.json";

const HELP_CONTENT_URL = "https://react-resources.s3.us-east-1.amazonaws.com/helpContent.json";

const demoRecord = () => JSON.parse(localStorage.getItem("plutonic-demo-therapist-progress") || "null") || {
  progress: {
    "learning:learning-overview": { area: "learning", itemId: "learning-overview", title: "Getting started", completed: true, openedAt: new Date().toISOString(), videoWatched: true },
    "policies:therapy-frequency-policy": { area: "policies", itemId: "therapy-frequency-policy", title: "Therapy frequency policy", openedAt: new Date().toISOString() },
  },
  uploads: [{ uploadId: "demo-upload", itemId: "license", itemName: "Professional license", fileName: "license.pdf", contentType: "application/pdf", size: 248000, uploadedAt: new Date().toISOString(), demo: true }],
  auditLog: [
    { timestamp: new Date().toISOString(), action: "session_joined", summary: "Joined Beach Therapy session" },
    { timestamp: new Date(Date.now() - 3600000).toISOString(), action: "avatar_setting", summary: "Enabled avatars for Alex Morgan", details: { clientName: "Alex Morgan", enabled: true } },
    { timestamp: new Date(Date.now() - 86400000).toISOString(), action: "session_scheduled", summary: "Scheduled Beach Therapy for Jordan Lee", details: { clientNames: ["Jordan Lee"] } },
  ],
};

const uploadUrl = (therapistId, uploadId) => `/api/admin/therapists/${encodeURIComponent(therapistId)}/uploads/${encodeURIComponent(uploadId)}`;

function StatusIcon({ kind, value }) {
  const definitions = {
    opened: value ? [MailOpen, "Opened"] : [Mail, "Not opened"],
    completed: value ? [CircleCheck, "Complete"] : [CircleX, "Incomplete"],
    watched: value ? [PlayCircle, "Watched"] : [Square, "Not watched"],
  };
  const [Icon, label] = definitions[kind];
  return <span tabIndex="0" aria-label={label} className={`group relative inline-flex h-8 w-8 items-center justify-center rounded-full ${value ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
    <Icon size={19} strokeWidth={2.25} />
    <span role="tooltip" className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block group-focus:block">{label}</span>
  </span>;
}

function sectionStatus(section, area, progress) {
  const examples = (section.content || []).map((block, index) => ({ block, index })).filter(({ block }) => block.type === "example");
  const rowItems = examples.map(({ block, index }) => ({
    id: `${section.id}-row-${index}`,
    title: block.need || `Row ${index + 1}`,
    completed: Boolean(progress[`${area}:${section.id}-row-${index}`]?.completed),
    videos: (block.media || []).filter((media) => media.type === "video").map(() => ({ watched: Boolean(progress[`${area}:${section.id}-row-${index}-video`]?.videoWatched) })),
  }));
  const topVideo = section.video ? [{ watched: Boolean(progress[`${area}:${section.id}-video`]?.videoWatched) }] : [];
  const videos = [...topVideo, ...rowItems.flatMap((row) => row.videos)];
  return {
    ...section,
    opened: Boolean(progress[`${area}:${section.id}`]?.openedAt),
    completed: rowItems.length ? rowItems.every((row) => row.completed) : Boolean(progress[`${area}:${section.id}`]?.completed),
    videos,
    rows: rowItems,
  };
}

function ProgressSection({ title, area, sections, progress, defaultOpen = false }) {
  const statuses = sections.map((section) => sectionStatus(section, area, progress));
  return <details open={defaultOpen} className="mt-3 overflow-visible rounded-xl border border-slate-200 bg-white">
    <summary className="cursor-pointer px-4 py-3 text-lg font-semibold marker:text-slate-500">{title}</summary>
    <div className="space-y-2 border-t border-slate-100 p-3">{statuses.map((section) => <div key={section.id} className="rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <strong className="leading-snug text-slate-800">{section.title}</strong>
        <div className="flex shrink-0 gap-1.5">
          <StatusIcon kind="opened" value={section.opened} />
          <StatusIcon kind="completed" value={section.completed} />
          {section.videos.length > 0 && <StatusIcon kind="watched" value={section.videos.every((video) => video.watched)} />}
        </div>
      </div>
      {section.rows.length > 0 && <div className="mt-3 divide-y divide-slate-100 rounded-lg bg-slate-50/70 px-3">{section.rows.map((row) => <div key={row.id} className="flex items-center justify-between gap-4 py-2.5 text-sm">
        <span className="leading-snug text-slate-700">{row.title}</span>
        <span className="flex shrink-0 gap-1.5"><StatusIcon kind="completed" value={row.completed} />{row.videos.length > 0 && <StatusIcon kind="watched" value={row.videos.every((video) => video.watched)} />}</span>
      </div>)}</div>}
    </div>)}</div>
  </details>;
}

const activityIcon = (action) => {
  if (action === "session_scheduled") return CalendarDays;
  if (action === "avatar_setting") return UserRound;
  if (action === "session_joined") return PlayCircle;
  if (action === "document_uploaded") return UploadCloud;
  return PlayCircle;
};

export default function AdminTherapists() {
  const [therapists, setTherapists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [catalogSections, setCatalogSections] = useState(helpContent);
  const learningSections = useMemo(() => learningContent.filter((section) => section.category === "learning" && section.showInLearningCenter !== false), []);
  const helpSections = useMemo(() => catalogSections.filter((section) => section.category === "help"), [catalogSections]);
  const policySections = useMemo(() => catalogSections.filter((section) => section.category === "policies"), [catalogSections]);

  useEffect(() => {
    if (isDemoMode) return;
    fetch(`${HELP_CONTENT_URL}?v=${Date.now()}`)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load help content");
        return response.json();
      })
      .then((sections) => {
        if (Array.isArray(sections)) setCatalogSections(sections);
      })
      .catch(() => {
        // Keep the bundled catalog as an offline fallback.
      });
  }, []);

  useEffect(() => {
    if (isDemoMode) {
      const record = demoRecord();
      setTherapists([{ id: "demo-therapist-local", name: "Demo Therapist", email: "therapist-demo@local.invalid", completedCount: Object.values(record.progress).filter((x) => x.completed).length, videoCount: Object.values(record.progress).filter((x) => x.videoWatched).length, uploadCount: record.uploads.length }]);
      return;
    }
    axios.get("/api/admin/therapists", { withCredentials: true }).then(({ data }) => setTherapists(data.therapists));
  }, []);

  const choose = async (row) => {
    setSelected(row);
    setPreview(null);
    if (isDemoMode) setDetail(demoRecord());
    else setDetail((await axios.get(`/api/admin/therapists/${row.id}`, { withCredentials: true })).data);
  };

  const openUpload = (item) => {
    if (item.demo) return;
    const url = uploadUrl(selected.id, item.uploadId);
    if (item.contentType?.startsWith("image/")) setPreview({ ...item, url });
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  return <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
    <div className="mx-auto max-w-7xl"><Link to="/admin" className="text-blue-700">← Admin dashboard</Link>
      <h1 className="my-6 text-3xl font-bold">Therapists</h1>
      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <div className="space-y-3">{therapists.map((row) => <button key={row.id} onClick={() => choose(row)} className={`flex w-full gap-3 rounded-xl border bg-white p-4 text-left shadow-sm ${selected?.id === row.id ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200"}`}>
          {row.headshotUploadId ? <img src={uploadUrl(row.id, row.headshotUploadId)} alt="" className="h-12 w-12 rounded-full object-cover" /> : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700"><UserRound size={25} /></span>}
          <span><strong className="block">{row.name}</strong><span className="text-sm text-slate-600">{row.email}</span><span className="mt-2 block text-xs text-slate-500">{row.completedCount} completed · {row.videoCount} videos · {row.uploadCount} uploads</span></span>
        </button>)}</div>
        <div className="rounded-xl bg-white p-5 shadow-sm">{!detail ? <p className="text-slate-500">Select a therapist to view progress, uploads, and recent activity.</p> : <>
          <h2 className="text-2xl font-semibold">{selected.name}</h2>
          <ProgressSection title="Learning Center" area="learning" sections={learningSections} progress={detail.progress || {}} defaultOpen />
          <ProgressSection title="Help Center" area="help" sections={helpSections} progress={detail.progress || {}} />
          <ProgressSection title="Policies" area="policies" sections={policySections} progress={detail.progress || {}} />
          <details className="mt-3 rounded-xl border border-slate-200 p-4"><summary className="cursor-pointer text-lg font-semibold">Uploads ({detail.uploads?.length || 0})</summary><div className="mt-3 space-y-2">{(detail.uploads || []).map((item) => <div key={item.uploadId} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3">
            <div><strong>{item.itemName}</strong><div className="text-sm text-slate-600"><button type="button" disabled={item.demo} onClick={() => openUpload(item)} className="font-medium text-blue-700 underline disabled:cursor-not-allowed disabled:text-slate-500">{item.fileName}</button> · {new Date(item.uploadedAt).toLocaleString()}</div>{item.demo && <div className="text-xs text-slate-500">Demo placeholder; real uploads open here.</div>}</div>
            {item.contentType?.startsWith("image/") ? <Image className="text-blue-600" /> : <FileText className="text-red-600" />}
          </div>)}</div></details>
          <details open className="mt-3 rounded-xl border border-slate-200 p-4"><summary className="cursor-pointer text-lg font-semibold">Recent activity ({detail.auditLog?.length || 0})</summary><ol className="mt-4 max-h-[44rem] space-y-3 overflow-y-auto pr-2">{(detail.auditLog || []).map((item, index) => { const Icon = activityIcon(item.action); return <li key={`${item.timestamp}-${index}`} className="flex gap-3 rounded-lg bg-slate-50 p-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700"><Icon size={19} /></span><span><span className="block font-medium">{item.summary}</span><time className="text-xs text-slate-500">{new Date(item.timestamp).toLocaleString()}</time></span></li>; })}</ol></details>
        </>}</div>
      </div>
    </div>
    {preview && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-6" role="dialog" aria-modal="true" aria-label={preview.fileName} onClick={() => setPreview(null)}><div className="relative max-h-full max-w-5xl rounded-xl bg-white p-4" onClick={(event) => event.stopPropagation()}><button onClick={() => setPreview(null)} className="absolute -right-3 -top-3 rounded-full bg-white p-2 shadow" aria-label="Close preview"><X /></button><img src={preview.url} alt={preview.fileName} className="max-h-[80vh] max-w-full object-contain" /><p className="mt-2 text-center text-sm text-slate-600">{preview.fileName}</p></div></div>}
  </div>;
}
